'use client';

import React from 'react';
import {
  MediaDeviceMenu,
  TrackToggle,
  useLocalParticipant,
  VideoTrack,
} from '@livekit/components-react';
import { isLocalTrack, LocalTrackPublication, Track, LocalTrack } from 'livekit-client';

type BackgroundType = 'none' | 'blur';

export function CameraSettings() {
  const [isClient, setIsClient] = React.useState(false);

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  const { cameraTrack, localParticipant } = useLocalParticipant();
  const [backgroundType, setBackgroundType] = React.useState<BackgroundType>(
    (cameraTrack as LocalTrackPublication)?.track?.getProcessor()?.name === 'background-blur'
      ? 'blur'
      : 'none',
  );

  const camTrackRef = React.useMemo(() => {
    if (cameraTrack?.track) {
      return {
        participant: localParticipant,
        publication: cameraTrack,
        source: Track.Source.Camera,
      };
    }
    return undefined;
  }, [cameraTrack, localParticipant]);

  const selectBackground = (type: BackgroundType) => {
    setBackgroundType(type);
  };

  React.useEffect(() => {
    if (isLocalTrack(cameraTrack?.track)) {
      const localTrack = cameraTrack.track as LocalTrack;
      if (backgroundType === 'blur') {
        import('@livekit/track-processors')
          .then(({ BackgroundBlur }) => {
            localTrack.setProcessor(BackgroundBlur());
          })
          .catch(console.error);
      } else {
        localTrack.stopProcessor();
      }
    }
  }, [cameraTrack, backgroundType]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {!isClient ? (
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            color: 'var(--lk-fg)',
            opacity: 0.7,
          }}
        >
          Loading camera settings...
        </div>
      ) : (
        <>
          {camTrackRef && (
            <div
              style={{
                background: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <VideoTrack
                style={{
                  width: '100%',
                  maxHeight: '200px',
                  objectFit: 'contain',
                  objectPosition: 'center',
                  transform: backgroundType === 'none' ? 'scaleX(-1)' : 'none',
                }}
                trackRef={camTrackRef}
              />
            </div>
          )}

          <div
            style={{
              padding: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '6px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <section className="lk-button-group" style={{ display: 'flex', gap: '8px' }}>
              <TrackToggle source={Track.Source.Camera}>Camera</TrackToggle>
              <div className="lk-button-group-menu">
                <MediaDeviceMenu kind="videoinput" />
              </div>
            </section>
          </div>

          <div
            style={{
              padding: '16px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <div
              style={{
                marginBottom: '12px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--lk-fg)',
              }}
            >
              Background Effects
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => selectBackground('none')}
                className="lk-button"
                aria-pressed={backgroundType === 'none'}
                style={{
                  border:
                    backgroundType === 'none'
                      ? '2px solid var(--lk-accent-bg)'
                      : '1px solid rgba(255, 255, 255, 0.2)',
                  minWidth: '80px',
                  height: '60px',
                  borderRadius: '6px',
                  background:
                    backgroundType === 'none'
                      ? 'rgba(59, 130, 246, 0.1)'
                      : 'rgba(255, 255, 255, 0.05)',
                  color: 'var(--lk-fg)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: '12px',
                  fontWeight: '500',
                }}
              >
                None
              </button>

              <button
                onClick={() => selectBackground('blur')}
                className="lk-button"
                aria-pressed={backgroundType === 'blur'}
                style={{
                  border:
                    backgroundType === 'blur'
                      ? '2px solid var(--lk-accent-bg)'
                      : '1px solid rgba(255, 255, 255, 0.2)',
                  minWidth: '80px',
                  height: '60px',
                  borderRadius: '6px',
                  background:
                    backgroundType === 'blur'
                      ? 'rgba(59, 130, 246, 0.1)'
                      : 'rgba(255, 255, 255, 0.05)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    filter: 'blur(8px)',
                    zIndex: 0,
                  }}
                />
                <span
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: 'white',
                    fontWeight: '500',
                  }}
                >
                  Blur
                </span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
